import {useQuery} from '@tanstack/react-query';
import {useBreakpointUp} from '@travel-frontend/core';
import Cookies from 'js-cookie';
import {Suspense, lazy} from 'react';
import {isDesktop} from 'react-device-detect';

import {ExitIntentLayerDefault} from '@/modules/common/components/ExitIntentLayerDefault';
import {useConfig} from '@/modules/common/context/Config';
import {useConsent} from '@/modules/common/context/Consent';
import {useLocalSplit} from '@/modules/common/hooks';
import * as bffSupportQueries from '@/modules/common/queries/bffSupport';

const ExitIntentLayerLeipzig = lazy(
  () => import('@/modules/common/components/ExitIntentLayerLeipzig')
);
const ExitIntentLayerNotLeipzig = lazy(
  () => import('@/modules/common/components/ExitIntentLayerNotLeipzig')
);

export const ExitLayer = () => {
  const consent = useConsent();
  const config = useConfig();
  const lgUp = useBreakpointUp('lg');

  const exitLayerEnabled = config.exitLayer.enabled;
  const exitLayerSplitEnabled =
    useLocalSplit('CAP_280_split_exit_layer').treatment === 'on';
  const enabled = exitLayerEnabled && exitLayerSplitEnabled;
  const requestsEnabled = enabled && consent.status?.consented;

  const metaQuery = useQuery(bffSupportQueries.meta());

  const BD4ID = Cookies.get('bd4ti') ?? '';
  const smartseerProfileQuery = useQuery({
    ...bffSupportQueries.smartseerProfile({BD4ID}),
    enabled: Boolean(BD4ID && requestsEnabled),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const sessionChurnRisk = smartseerProfileQuery.data?.intent?.sessionChurnRisk;
  const userCity = metaQuery.data?.userCity ?? 'Unknown';
  const newsletterTrackingSource = lgUp
    ? config.newsletterBanner?.trackingSource.desktop
    : config.newsletterBanner?.trackingSource.mobile;

  const forcedTreatment = localStorage.getItem(
    'forceExperimentationExitIntentLayer'
  );
  const forceTreatmentA = forcedTreatment === 'A';
  const forceTreatmentB = forcedTreatment === 'B';
  const forceTreatmentDefault = forcedTreatment === 'default';
  const hasForcedTreatment =
    forceTreatmentA || forceTreatmentB || forceTreatmentDefault;

  if (
    !isDesktop ||
    !enabled ||
    !consent.status?.consented ||
    (!hasForcedTreatment &&
      (metaQuery.isPending || smartseerProfileQuery.isPending))
  ) {
    return null;
  }

  const conditionForTreatmentA =
    sessionChurnRisk != null &&
    sessionChurnRisk > 0.5 &&
    userCity === 'Leipzig';

  if (forceTreatmentA || (conditionForTreatmentA && !hasForcedTreatment)) {
    return (
      <Suspense>
        <ExitIntentLayerLeipzig userCity={userCity} />
      </Suspense>
    );
  }

  const conditionForTreatmentB =
    sessionChurnRisk != null &&
    sessionChurnRisk > 0.5 &&
    userCity !== 'Leipzig' &&
    newsletterTrackingSource;

  if (forceTreatmentB || (conditionForTreatmentB && !hasForcedTreatment)) {
    return (
      <Suspense>
        <ExitIntentLayerNotLeipzig
          userCity={userCity}
          newsletterTrackingSource={newsletterTrackingSource ?? ''}
        />
      </Suspense>
    );
  }

  return <ExitIntentLayerDefault />;
};
